import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogTitle from '@mui/material/DialogTitle'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import React, { useState } from 'react'
import SaveButton from '../Buttons/SaveButton'
import CloseButton from '../IconButtons/CloseButton'

/**
 * The extra props this HOC adds vary in shape depending on the wrapped modal, so they
 * are modelled as a loose record with the fields this file itself relies on made explicit.
 */
type SimpleModalProps = {
  onClose?(value?: unknown): void
  noEscClose?: boolean
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false
  fullWidth?: boolean
  header?: React.ReactNode
  allowSubmit?: React.ReactNode
  valid?: boolean
  backgroundColor?: string
  [key: string]: unknown
}

/**
 * Simple modal higher order component.
 *
 * @param ModalComponent - Modal component to wrap.
 * @returns Simple modal component.
 * @notExported
 */
const withSimpleModal = <TModalProps extends object>(ModalComponent: React.ComponentType<TModalProps>) => {
  const SimpleModal = (props: SimpleModalProps) => {
    const theme = useTheme()
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
    const [submitLocally, setSubmitLocally] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(false)
    const [buttonLoading, setButtonLoading] = useState<boolean>(false)

    return (
      <Dialog
        fullScreen={fullScreen}
        open
        aria-labelledby="form-dialog-title"
        onClose={() => {
          if (props.noEscClose) {
            return
          }
          props.onClose?.({})
        }}
        maxWidth={props.maxWidth}
        fullWidth={props.fullWidth}
      >
        <DialogTitle
          sx={{ backgroundColor: props.backgroundColor ? props.backgroundColor : theme.palette.background.paper }}
        >
          <span id="form-dialog-title">{props.header}</span>
          <CloseButton testId="button-with-simple-modal-close" clickAction={() => props.onClose?.({})} />
        </DialogTitle>
        {React.createElement(ModalComponent, {
          ...props,
          submitIndicator: submitLocally,
          resetSubmit: () => {
            setSubmitLocally(false)
            setButtonLoading(false)
          },
          setLoading,
          setSubmitLocally,
        } as unknown as React.Attributes & TModalProps)}
        <DialogActions
          sx={{
            background: props.backgroundColor ? props.backgroundColor : theme.palette.background.paper,
            paddingRight: '22px',
            paddingBottom: '22px',
          }}
        >
          {props.allowSubmit && (
            <SaveButton
              label={loading ? <CircularProgress /> : props.allowSubmit}
              valid={props.valid === undefined ? true : props.valid}
              clickAction={() => {
                setButtonLoading(true)
                setSubmitLocally(true)
              }}
              disabled={loading}
              loading={buttonLoading}
              testId="button-with-simple-modal-submit"
            />
          )}
        </DialogActions>
      </Dialog>
    )
  }
  return SimpleModal
}

export default withSimpleModal
